import { cn, Flex } from '@bridebook/aura';
import { IsPreviewWrapperProvider } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/config/preview-wrapper-context';
import { DesktopPreviewDevice } from '@bridebook/web/lib/wedding-website/desktop/types';
import { useMeasure } from '@uidotdev/usehooks';
import { CSSProperties, PropsWithChildren, useEffect, useState } from 'react';

const DESKTOP_WIDTH = 1240;
const PHONE_HEIGHT = 800;

/**
 * Fits the fixed-width desktop render into the panel, or `null` while the width is unknown.
 */
const desktopScaleFor = (availableWidth: number | undefined): number | null =>
  !availableWidth || availableWidth <= 0 ? null : Math.min(1, availableWidth / DESKTOP_WIDTH);

/**
 * Fits the fixed-height phone frame into its space, or `null` while unknown.
 */
const phoneScaleFor = (availableHeight: number | null): number | null => {
  if (availableHeight === null) return null;
  if (availableHeight <= 0) return 1;
  return Math.min(1, availableHeight / PHONE_HEIGHT);
};

type Props = {
  activePreviewDevice: DesktopPreviewDevice;
  availableWidth?: number;
  className?: string;
  desktopScaling?: boolean;
};
export const WebsitePreviewWrapper = ({
  children,
  activePreviewDevice,
  availableWidth,
  className,
  desktopScaling = false,
}: PropsWithChildren<Props>) => {
  const [wrapperRef, { height: availableHeight }] = useMeasure<HTMLDivElement>();
  const phonePreviewScale = phoneScaleFor(availableHeight);
  const desktopPreviewScale = desktopScaleFor(availableWidth);

  const isMobilePreview = activePreviewDevice === DesktopPreviewDevice.Mobile;

  // Only the transform a surface actually applies has to be measured before the first paint.
  const isScaleReady = () => {
    if (isMobilePreview) return phonePreviewScale !== null;
    if (desktopScaling) return desktopPreviewScale !== null;
    return true; // desktop unscaled (full-page modal, RSVP preview): nothing to measure
  };
  const awaitingScale = !isScaleReady();

  const phoneScale = phonePreviewScale ?? 1;
  const desktopScale = desktopPreviewScale ?? 1;

  // Off until a scale has been applied, so the first one lands instantly and only later changes (device
  // toggle, resize) animate. Enabled a frame later so the scaled state is painted before the transition
  // exists to animate it.
  const [transitionsEnabled, setTransitionsEnabled] = useState(false);
  useEffect(() => {
    if (awaitingScale) return;
    const frame = requestAnimationFrame(() => setTransitionsEnabled(true));
    return () => cancelAnimationFrame(frame);
  }, [awaitingScale]);

  return (
    <Flex
      ref={wrapperRef}
      id="preview-wrapper"
      className={cn('min-h-0 w-full flex-1 justify-center', desktopScaling && 'w-auto')}>
      <div
        id="preview-scroll-container"
        style={
          {
            '--phone-preview-scale': phoneScale,
            '--phone-height': `${PHONE_HEIGHT}px`,
            '--desktop-preview-scale': desktopScale,
            '--desktop-width': `${DESKTOP_WIDTH}px`,
            '--desktop-height': `${(availableHeight ?? 0) / desktopScale}px`,
            // Canvas height of the previewed device — consumed by the Motif layout as `--ww-height` so full-viewport
            // heroes/sections fit one previewed screen (the real `100svh` is the browser window, wrong in a scaled
            // panel and in the phone-shaped preview). Unset for the unscaled full-page modal → Motif falls back to 100svh.
            '--ww-canvas-height':
              activePreviewDevice === DesktopPreviewDevice.Mobile
                ? 'var(--phone-height)'
                : desktopScaling
                  ? 'var(--desktop-height)'
                  : undefined,
          } as CSSProperties
        }
        className={cn(
          '@container origin-top overflow-y-auto rounded-3xl bg-white',
          // Laid out but not painted until scaled, so the first visible frame is already the right size.
          awaitingScale && 'invisible',
          transitionsEnabled && 'transition-all duration-300',
          activePreviewDevice === DesktopPreviewDevice.Mobile &&
            'scrollbar-hidden h-[var(--phone-height)] min-h-[var(--phone-height)] w-[360px] scale-[var(--phone-preview-scale)] border-8 border-space-15',
          activePreviewDevice === DesktopPreviewDevice.Desktop &&
            'h-full w-full rounded-none border-white scrollbar-thin',
          activePreviewDevice === DesktopPreviewDevice.Desktop &&
            desktopScaling &&
            `h-[var(--desktop-height)] min-h-[var(--desktop-height)] w-[var(--desktop-width)] scale-[var(--desktop-preview-scale)]`,
          className,
        )}>
        <IsPreviewWrapperProvider value={true}>{children}</IsPreviewWrapperProvider>
      </div>
    </Flex>
  );
};
